import React, { useState, useRef, useEffect } from "react";

const UploadField = ({
  name,
  label,
  accept = ["image/*", ".pdf", ".ppt", ".pptx"],
  register,
  setValue,
  required = false,
  maxFiles = 1,
  onChangeFiles,
  error,
  // Archivos que el item ya tiene guardados (modo edición).
  // Se esperan objetos { name, url, thumb?, size? } o strings con la URL.
  existingFiles = [],
}) => {
  const [files, setFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previews, setPreviews] = useState([]);
  const inputRef = useRef(null);

  // Componemos el ref de react-hook-form con nuestro ref local,
  // para no pisarlo (register() devuelve un ref que debe conservarse).
  const rhfRegister = register
    ? register(name, {
        required: required ? `${label} es obligatorio` : false,
      })
    : null;

  const setInputRef = (element) => {
    inputRef.current = element;
    if (!rhfRegister) return;
    const { ref } = rhfRegister;
    if (typeof ref === "function") {
      ref(element);
    } else if (ref && typeof ref === "object") {
      ref.current = element;
    }
  };

  useEffect(() => {
    const newPreviews = files
      .filter((file) => file.type && file.type.startsWith("image/"))
      .map((file) => ({
        file,
        url: URL.createObjectURL(file),
      }));
    setPreviews(newPreviews);

    return () => {
      newPreviews.forEach((preview) => {
        URL.revokeObjectURL(preview.url);
      });
    };
  }, [files]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const fileNameFromUrl = (url) => {
    const clean = String(url).split("?")[0].split("#")[0];
    const name = clean.substring(clean.lastIndexOf("/") + 1);
    try {
      return decodeURIComponent(name) || String(url);
    } catch (error) {
      return name || String(url);
    }
  };

  const isImageName = (fileName = "") =>
    /\.(png|jpe?g|gif|webp|bmp|svg|avif|ico)$/i.test(
      String(fileName).split("?")[0],
    );

  // Normaliza la prop existingFiles a una lista de objetos pintables.
  const normalizedExistingFiles = (existingFiles || []).map((file) =>
    typeof file === "string"
      ? { name: fileNameFromUrl(file), url: file, thumb: file, size: null }
      : {
          name: file.name || fileNameFromUrl(file.url),
          url: file.url,
          thumb: file.thumb ?? file.url,
          size: file.size ?? null,
        },
  );

  const getFileIcon = (file) => {
    const fileName = (file.name || "").toLowerCase();
    if (
      (file.type && file.type.startsWith("image/")) ||
      isImageName(file.name)
    ) {
      return "🖼️";
    }
    if (file.type === "application/pdf" || fileName.endsWith(".pdf")) {
      return "📄";
    }
    if (
      file.type === "application/vnd.ms-powerpoint" ||
      file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
      fileName.endsWith(".ppt") ||
      fileName.endsWith(".pptx")
    ) {
      return "📊";
    }
    return "📁";
  };

  const isValidFileType = (file) => {
    if (!accept || accept.length === 0) return true;

    const fileName = file.name.toLowerCase();
    const fileType = file.type;

    return accept.some((type) => {
      if (type.startsWith(".")) {
        return fileName.endsWith(type);
      }
      if (type.endsWith("/*")) {
        const baseType = type.split("/")[0];
        return fileType.startsWith(baseType + "/");
      }
      return fileType === type;
    });
  };

  const handleFiles = (newFiles) => {
    const validFiles = Array.from(newFiles).filter(isValidFileType);

    if (validFiles.length === 0) return;

    let updatedFiles = [];
    if (maxFiles === 1) {
      updatedFiles = [validFiles[0]];
    } else {
      updatedFiles = [...files, ...validFiles].slice(0, maxFiles);
    }

    setFiles(updatedFiles);

    if (setValue) {
      if (maxFiles === 1) {
        setValue(name, updatedFiles[0] || null, { shouldValidate: true });
      } else {
        const dataTransferFiles = new DataTransfer();
        updatedFiles.forEach((file) => {
          dataTransferFiles.items.add(file);
        });
        setValue(name, dataTransferFiles.files, { shouldValidate: true });
      }
    }

    if (onChangeFiles) {
      onChangeFiles(updatedFiles);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e) => {
    // Notificamos también a react-hook-form (si register está activo)
    if (rhfRegister && rhfRegister.onChange) {
      rhfRegister.onChange(e);
    }

    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = "";
    }
  };

  // Guarda anti-recursión: el click sintético del input burbujea y podría
  // volver a disparar handleZoneClick en el mismo task.
  const openingRef = useRef(false);

  const openFileExplorer = () => {
    if (openingRef.current || !inputRef.current) return;
    openingRef.current = true;
    try {
      inputRef.current.click();
    } finally {
      // Reinicia en el siguiente task, cuando ya terminó la propagación.
      setTimeout(() => {
        openingRef.current = false;
      }, 0);
    }
  };

  // Un solo disparador: el click en cualquier punto del dropzone
  // abre el explorador. El botón es meramente visual (pointer-events: none),
  // evitando así la doble apertura del diálogo.
  const handleZoneClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openFileExplorer();
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(updatedFiles);

    if (setValue) {
      if (maxFiles === 1) {
        setValue(name, updatedFiles[0] || null, { shouldValidate: true });
      } else {
        const dataTransferFiles = new DataTransfer();
        updatedFiles.forEach((file) => {
          dataTransferFiles.items.add(file);
        });
        setValue(name, dataTransferFiles.files, { shouldValidate: true });
      }
    }

    if (onChangeFiles) {
      onChangeFiles(updatedFiles);
    }
  };

  const acceptedTypesText = accept
    .map((type) => {
      if (type === "image/*") return "Imágenes";
      if (type === ".pdf" || type === "application/pdf") return "PDF";
      if (type === ".ppt" || type === ".pptx") return "PowerPoint";
      return type;
    })
    .join(", ");

  return (
    <div className="group" style={{ width: "100%" }}>
      {label && <label htmlFor={name}>{label}</label>}

      {/* Input fuera del dropzone: así su click sintético NO burbujea
          hacia el onClick del dropzone (evita recursión infinita). */}
      <input
        ref={setInputRef}
        type="file"
        id={name}
        accept={accept.join(",")}
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
        onChange={handleInputChange}
        onClick={(e) => e.stopPropagation()}
      />

      <div
        className={`upload-field ${isDragActive ? "upload-field--active" : ""}`}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleZoneClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openFileExplorer();
          }
        }}
        style={{
          border: isDragActive ? "2px dashed #2196f3" : "2px dashed #ccc",
          borderRadius: "8px",
          padding: "2rem",
          textAlign: "center",
          backgroundColor: isDragActive ? "#f5f9ff" : "#fafafa",
          cursor: "pointer",
          transition: "all 0.3s ease",
          minHeight: "150px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
          position: "relative",
        }}
      >

        <div style={{ marginBottom: "1rem" }}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: "#666" }}
          >
            <path
              d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04Z"
              fill="#999"
            />
          </svg>
        </div>

        <p style={{ margin: "0 0 0.5rem 0", color: "#333", fontWeight: "500" }}>
          Arrastra y suelta archivos aquí
        </p>

        <p style={{ margin: "0 0 1rem 0", color: "#666", fontSize: "0.9rem" }}>
          o
        </p>

        <span
          style={{
            backgroundColor: "var(--color-secondary-orange)",
            color: "white",
            border: "none",
            padding: "0.75rem 1.5rem",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "0.95rem",
            fontWeight: "500",
            transition: "background-color 0.3s",
            pointerEvents: "none",
            display: "inline-block",
          }}
        >
          Seleccionar archivo
        </span>

        <small
          style={{
            marginTop: "1rem",
            color: "#888",
            fontSize: "0.85rem",
            display: "block",
          }}
        >
          Archivos permitidos: {acceptedTypesText}
        </small>
      </div>

      {files.length > 0 && (
        <div
          style={{
            marginTop: "1rem",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "1rem",
            backgroundColor: "#fff",
          }}
        >
          <h4 style={{ margin: "0 0 0.75rem 0", fontSize: "0.95rem", color: "#333" }}>
            Archivos seleccionados ({files.length})
          </h4>

          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {files.map((file, index) => (
              <li
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem",
                  border: "1px solid #f0f0f0",
                  borderRadius: "6px",
                  marginBottom: index < files.length - 1 ? "0.5rem" : "0",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <span style={{ fontSize: "1.5rem" }}>{getFileIcon(file)}</span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.95rem",
                        fontWeight: "500",
                        color: "#333",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </p>
                    <small style={{ color: "#666", fontSize: "0.85rem" }}>
                      {formatFileSize(file.size)}
                    </small>
                  </div>

                  {previews.some((p) => p.file === file) && (
                    <img
                      src={previews.find((p) => p.file === file)?.url}
                      alt="Preview"
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                  )}
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "#f44336",
                    cursor: "pointer",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: "0.5rem",
                  }}
                  title="Eliminar archivo"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                      fill="#f44336"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Archivos que el item ya tiene guardados (solo se muestran
          mientras no se hayan seleccionado archivos nuevos). */}
      {files.length === 0 && normalizedExistingFiles.length > 0 && (
        <div
          style={{
            marginTop: "1rem",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "1rem",
            backgroundColor: "#fff",
          }}
        >
          <h4
            style={{
              margin: "0 0 0.75rem 0",
              fontSize: "0.95rem",
              color: "#333",
            }}
          >
            {normalizedExistingFiles.length === 1
              ? "Archivo actual"
              : `Archivos actuales (${normalizedExistingFiles.length})`}
          </h4>

          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {normalizedExistingFiles.map((file, index) => (
              <li
                key={file.url || index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem",
                  border: "1px solid #f0f0f0",
                  borderRadius: "6px",
                  marginBottom:
                    index < normalizedExistingFiles.length - 1
                      ? "0.5rem"
                      : "0",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <span style={{ fontSize: "1.5rem" }}>
                    {getFileIcon(file)}
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.95rem",
                        fontWeight: "500",
                        color: "#333",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </p>
                    <small style={{ color: "#666", fontSize: "0.85rem" }}>
                      {file.size !== null && file.size !== undefined
                        ? formatFileSize(file.size)
                        : "Archivo almacenado"}
                    </small>
                  </div>

                  {file.thumb && isImageName(file.thumb) && (
                    <img
                      src={file.thumb}
                      alt="Vista previa del archivo actual"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                  )}
                </div>

                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Abrir archivo en una pestaña nueva"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    marginLeft: "0.5rem",
                    color: "var(--color-secondary-orange, #f57c00)",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Abrir
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p style={{ color: "#d32f2f", fontSize: "0.875rem", marginTop: "0.5rem" }}>
          {error.message || error}
        </p>
      )}
    </div>
  );
};

export default UploadField;